'use client';
import { useId, useState, type ReactNode } from 'react';
import type { DateRangeFilterProps, InstantRangePreset, RangePreset } from 'elowen-plugin-ui-kit';
import { CalendarDays, ChevronDown } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { instantRangeBounds, instantToLocalInput, localInputToInstant } from '../../lib/dateRange';
import { cn } from '../../lib/utils';
import { Input } from './Input';
import { Popover, PopoverContent, PopoverTrigger } from './shadcn/popover';
import { selectTriggerVariants } from './shadcn/select';

const DAY_PRESETS: RangePreset[] = ['today', '7d', '30d', '90d', 'all', 'custom'];
const INSTANT_PRESETS: InstantRangePreset[] = ['1h', '6h', '24h', '7d', '30d', 'custom'];

/** One shared picker surface; precision changes the typed model and custom fields, not the UI grammar. */
function RangePicker<P extends string>({ open, onOpenChange, label, compact, preset, presets, labels, onPick, children }: {
  open: boolean; onOpenChange: (open: boolean) => void; label: string; compact: boolean;
  preset: P; presets: P[]; labels: Record<P, string>; onPick: (preset: P) => void; children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" className={cn(selectTriggerVariants(), 'inline-flex w-auto')}>
          <CalendarDays size={14} aria-hidden className="text-muted-foreground" />
          <span className={`${compact ? 'max-w-36' : 'max-w-56'} truncate`}>{label}</span>
          <ChevronDown size={14} aria-hidden className="shrink-0 opacity-60 transition-transform group-data-[state=open]:rotate-180" />
        </button>
      </PopoverTrigger>
      <PopoverContent role="dialog" aria-label={t.common.rangeLabel} align="end" className="w-64 p-3">
        <div className="flex flex-col gap-1">
          {presets.filter((p) => p !== 'custom').map((p) => (
            <button key={p} type="button" onClick={() => onPick(p)} aria-pressed={preset === p}
              className="flex items-center justify-between rounded-md px-2.5 py-1.5 text-left text-ui text-foreground transition-colors hover:bg-accent hover:text-accent-foreground aria-pressed:bg-primary/10 aria-pressed:text-primary">
              {labels[p]}
            </button>
          ))}
        </div>
        {presets.some((p) => p === 'custom') && (
          <div className="mt-3 border-t border-border pt-3">
            <p className="mb-2 text-eyebrow uppercase text-muted-foreground">{t.common.rangeCustom}</p>
            {children}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function DayRangeFilter({ value, onChange, compact = false, presets = DAY_PRESETS }: Extract<DateRangeFilterProps, { precision?: 'day' }>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const labels: Record<RangePreset, string> = {
    '7d': t.common.rangeLast7, '30d': t.common.rangeLast30, '90d': t.common.rangeLast90,
    today: t.common.rangeToday, all: t.common.rangeAll, custom: t.common.rangeCustom,
  };
  const label = value.preset === 'custom' ? `${value.from ?? '…'} – ${value.to ?? '…'}` : labels[value.preset];
  return (
    <RangePicker open={open} onOpenChange={setOpen} label={label} compact={compact} preset={value.preset} presets={presets} labels={labels}
      onPick={(preset) => { onChange({ preset, from: null, to: null }); setOpen(false); }}>
      <label className="mb-2 flex items-center gap-2 text-small text-muted-foreground">
        <span className="w-7 shrink-0">{t.common.rangeFrom}</span>
        <Input type="date" value={value.from ?? ''} max={value.to ?? undefined}
          onChange={(e) => onChange({ preset: 'custom', from: e.target.value || null, to: value.to })} />
      </label>
      <label className="flex items-center gap-2 text-small text-muted-foreground">
        <span className="w-7 shrink-0">{t.common.rangeTo}</span>
        <Input type="date" value={value.to ?? ''} min={value.from ?? undefined}
          onChange={(e) => onChange({ preset: 'custom', from: value.from, to: e.target.value || null })} />
      </label>
    </RangePicker>
  );
}

function InstantRangeFilter({ value, onChange, compact = false, presets = INSTANT_PRESETS, min, max }: Extract<DateRangeFilterProps, { precision: 'instant' }>) {
  const { t, locale } = useTranslation();
  const [open, setOpen] = useState(false);
  const localBounds = () => ({ from: value.from ? instantToLocalInput(value.from) : '', to: value.to ? instantToLocalInput(value.to) : '' });
  const [draft, setDraft] = useState(localBounds);
  const [edited, setEdited] = useState(false);
  const errorId = useId();
  const labels: Record<InstantRangePreset, string> = {
    '1h': t.common.rangeLastHour, '6h': t.common.rangeLast6Hours, '24h': t.common.rangeLast24Hours,
    '7d': t.common.rangeLast7, '30d': t.common.rangeLast30, custom: t.common.rangeCustom,
  };
  // Preserve an existing repeated-hour instant until that field is edited. Date's local parser
  // deliberately picks the earlier occurrence for a newly entered ambiguous wall time.
  const resolve = (next: typeof draft) => {
    const from = value.from && next.from === instantToLocalInput(value.from) ? value.from : localInputToInstant(next.from);
    const to = value.to && next.to === instantToLocalInput(value.to) ? value.to : localInputToInstant(next.to);
    if (!next.from || !next.to) return { valid: false, error: 'incomplete' } as const;
    if (!from || !to) return { valid: false, error: 'invalid' } as const;
    return instantRangeBounds({ from, to }, { min, max });
  };
  const bounds = resolve(draft);
  const errors = {
    incomplete: t.common.rangeIncomplete, invalid: t.common.rangeInvalidInstant,
    order: t.common.rangeInvalidOrder, bounds: t.common.rangeOutsideBounds,
  };
  const error = edited && !bounds.valid ? errors[bounds.error] : undefined;
  const edit = (next: typeof draft) => {
    setDraft(next);
    setEdited(true);
    const resolved = resolve(next);
    if (resolved.valid) onChange({ preset: 'custom', from: resolved.from, to: resolved.to });
  };
  const format = (iso: string | null) => iso ? new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'medium' }).format(new Date(iso)) : '…';
  const label = value.preset === 'custom' ? `${format(value.from)} – ${format(value.to)}` : labels[value.preset];
  return (
    <RangePicker open={open} onOpenChange={(next) => {
      if (next) { setDraft(localBounds()); setEdited(false); }
      setOpen(next);
    }} label={label} compact={compact} preset={value.preset} presets={presets} labels={labels}
      onPick={(preset) => { onChange({ preset, from: null, to: null }); setOpen(false); }}>
      {(['from', 'to'] as const).map((side) => (
        <label key={side} className="mb-2 flex min-w-0 flex-col gap-1 text-small text-muted-foreground">
          <span>{side === 'from' ? t.common.rangeFrom : t.common.rangeTo}</span>
          <Input type="datetime-local" step="0.001" value={draft[side]}
            min={min ? instantToLocalInput(min) : undefined} max={max ? instantToLocalInput(max) : undefined}
            aria-invalid={error ? true : undefined} aria-describedby={error ? errorId : undefined}
            onChange={(e) => edit({ ...draft, [side]: e.target.value })} className="min-w-0" />
        </label>
      ))}
      {error ? <p id={errorId} role="alert" className="text-meta text-destructive">{error}</p> : null}
    </RangePicker>
  );
}

/** Omitted precision preserves the existing calendar-day picker and open custom bounds. */
export function DateRangeFilter(props: DateRangeFilterProps) {
  return props.precision === 'instant' ? <InstantRangeFilter {...props} /> : <DayRangeFilter {...props} />;
}
